import { useTheme } from '@mui/material/styles';
import { BaseHighchart } from './BaseHighchart';

/*
 * SankeyChart
 * ------------
 * Minimal wrapper around Highcharts Sankey that matches your API style.
 *
 * Props:
 *   data: Array<{ from: string, to: string, weight: number, color?: string }>
 *   legend: 'right' | 'bottom' | null/undefined (hidden when null/undefined)
 *   dataLabels: boolean
 *   nodeWidth?: number       // optional override for node width
 *   nodePadding?: number     // optional override for vertical spacing between nodes
 *   linkOpacity?: number     // optional override for link transparency
 *
 * Notes:
 * - Stable series id and point ids for smooth animations on updates.
 * - Theme-aware link colors and node outlines using MUI palette.divider.
 * - Background stays transparent; BaseHighchart handles sizing, fonts, and theme colors.
 */
// charts/SankeyChart.jsx


export const SankeyChart = ({
  data = [],
  legend,
  dataLabels = true,
  nodeWidth = 20,
  nodePadding = 10,
  linkOpacity = 0.5,
}) => {
  const theme = useTheme();
  const dividerColor = theme.palette.divider;
  const textColor = theme.palette.text.primary;

  if (!data.length) return null;

  // Build Highcharts-compatible Sankey link objects
  const links = data.map((item, i) => ({
    id: `${item.from}→${item.to}__${i}`,
    from: item.from,
    to: item.to,
    weight: item.weight,
    color: item.color, // 👈 allow per-link color
  }));

  const legendOptions =
    legend == null
      ? { enabled: false }
      : legend === 'bottom'
        ? { enabled: true, align: 'center', verticalAlign: 'bottom' }
        : { enabled: true, layout: 'vertical', align: 'right', verticalAlign: 'middle' };

  const options = {
    chart: {
      type: 'sankey',
      spacing: [8, 8, 24, 8],
      animation: { duration: 300 },
      backgroundColor: 'transparent',
    },
    title: { text: null },
    credits: { enabled: false },
    legend: legendOptions,

    tooltip: {
      pointFormat:
        '<b>{point.fromNode.name}</b> → <b>{point.toNode.name}</b>: <b>{point.weight:,.0f}</b>',
      shared: false,
    },

    plotOptions: {
      series: {
        animation: { duration: 300 },
        borderWidth: 0,
        turboThreshold: 0,
        dataLabels: {
          enabled: dataLabels,
          style: {
            color: textColor,
            textOutline: 'none',
            fontSize: '0.8rem',
          },
        },
        states: {
          hover: {
            brightness: 0.1,
          },
        },
      },
      sankey: {
        curveFactor: 0.5,
        nodeWidth,
        nodePadding,
        borderColor: dividerColor,
        linkOpacity,
        colorByPoint: false, // 👈 keep explicit colors from data
      },
    },

    series: [
      {
        id: 'sankey',
        type: 'sankey',
        name: 'Flow',
        keys: ['from', 'to', 'weight', 'color'], // 👈 include color
        data: links.map(l => [l.from, l.to, l.weight, l.color]),
        showInLegend: !!legendOptions.enabled,
      },
    ],
  };

  return <BaseHighchart options={options} />;
};

